'use client';

import * as React from 'react';
import Masonry from 'react-masonry-css';
import { Button, InputGroup, Modal, ProgressBar, Tabs } from '@heroui/react';
import { ImageCropper } from '@hs/ui';
import type { ImageCropperNormRect, ImageCropperRef } from '@hs/ui';
import { IconPhotoPlus, IconSearch } from '@tabler/icons-react';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';
import { trpc } from '@/lib/trpc';
import { ensureAnonymousAuth, getMediaTypeForMedia, uploadFileDirect } from '@hs/shared';

const CLOUDBASE_ENV_ID = 'his-1g5d6vlz6368064f';
/** 与海报页 `POSTER_PAGE_SIZE` 一致，保证单次加载条数相同 */
const GALLERY_PAGE_SIZE = 12;

/**
 * `react-masonry-css` 的 `breakpointCols` 对象是按 **视口宽度** 断档的，不会看父容器；
 * 弹窗很窄时只要浏览器窗口够宽仍会落到 `default: 8`。
 * 这里按 **滚动容器宽度** 计算列数，保证小窗内列数合理。
 */
function masonryColumnsForContainerWidth(width: number): number {
  if (width < 1) return 1;
  if (width >= 720) return 4;
  if (width >= 520) return 3;
  if (width >= 380) return 2;
  return 1;
}

type TabKey = 'gallery' | 'upload';

type MediaItem = {
  id: unknown;
  url?: string | null;
  name?: string | null;
};

type UploadTabState =
  | { status: 'idle' }
  | { status: 'uploading'; file: File; percent: number }
  | { status: 'error'; file: File; message: string };

function useObjectUrl(file: File | null): string | null {
  const [url, setUrl] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url;
}

const checkerboardStyle: React.CSSProperties = {
  backgroundColor: '#e5e5e5',
  backgroundImage: [
    'linear-gradient(45deg, #d4d4d4 25%, transparent 5%)',
    'linear-gradient(-45deg, #d4d4d4 25%, transparent 5%)',
    'linear-gradient(45deg, transparent 75%, #d4d4d4 75%)',
    'linear-gradient(-45deg, transparent 75%, #d4d4d4 75%)',
  ].join(', '),
  backgroundSize: '8px 8px',
  backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
};

/** 与 `ImageCropper` 归一化裁切一致：相对原图 0–1 的 x,y,w,h */
export type SelectMyImageCropNorm = ImageCropperNormRect;

export type SelectMyImagePick = {
  url: string;
  /** 未要求比例裁切时为 `null` */
  cropNorm: SelectMyImageCropNorm | null;
};

export type SelectMyImageListDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 从我的图库选中或上传成功得到 URL 时触发；是否关闭弹窗由调用方决定 */
  onImageUrlChosen: (url: string) => void;
};

export type SelectMyImageGalleryPanelProps = {
  /** 是否请求列表（例如仅图库 Tab 激活时为 true） */
  active: boolean;
  /**
   * 承载列表的弹层是否打开；从 false→true 时重置分页与累计结果（切换 Tab 不应触发重置）。
   */
  listHostOpen: boolean;
  searchKeyword: string;
  onPickImage: (url: string) => void;
  /**
   * 无限滚动 IntersectionObserver 的 `root`，须为实际滚动的祖先节点。
   * 不传时在面板内使用自带 `overflow-y-auto`（适合 `SelectMyImageListDialog` 图库 Tab）。
   */
  scrollRootRef?: React.RefObject<HTMLElement | null>;
  /** 与外部 `scrollRootRef` 联用时包裹瀑布流的额外 class（如留白已由外层提供则留空） */
  innerClassName?: string;
};

/**
 * 「我的图库」瀑布流 + 无限滚动 + 搜索关键词过滤，与 `ImageSelectPopover` / `SelectMyImageListDialog` 共用。
 */
export function SelectMyImageGalleryPanel({
  active,
  listHostOpen,
  searchKeyword,
  onPickImage,
  scrollRootRef,
  innerClassName,
}: SelectMyImageGalleryPanelProps) {
  const [page, setPage] = React.useState(1);
  const [accumulated, setAccumulated] = React.useState<MediaItem[]>([]);
  const loadMoreSentinelRef = React.useRef<HTMLDivElement>(null);
  const measureRef = React.useRef<HTMLDivElement>(null);
  const [internalScrollEl, setInternalScrollEl] = React.useState<HTMLDivElement | null>(null);
  const loadMoreGateRef = React.useRef({ hasNext: false, isFetching: false });
  const [masonryColumnCount, setMasonryColumnCount] = React.useState(3);
  const [loadedImageIds, setLoadedImageIds] = React.useState(() => new Set<string>());
  const prevListHostOpenRef = React.useRef(false);

  React.useEffect(() => {
    if (listHostOpen && !prevListHostOpenRef.current) {
      setPage(1);
      setAccumulated([]);
      setLoadedImageIds(new Set());
    }
    prevListHostOpenRef.current = listHostOpen;
  }, [listHostOpen]);

  React.useEffect(() => {
    setPage(1);
    setAccumulated([]);
    setLoadedImageIds(new Set());
  }, [searchKeyword]);

  React.useLayoutEffect(() => {
    if (!active) return;
    const el = measureRef.current;
    if (!el) return;
    let rafId: number | null = null;
    const update = () => {
      setMasonryColumnCount(masonryColumnsForContainerWidth(el.clientWidth));
    };
    update();
    rafId = window.requestAnimationFrame(update);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, [active]);

  const query = trpc.assets.listMy.useQuery(
    {
      page,
      pageSize: GALLERY_PAGE_SIZE,
      type: 'image',
      where: searchKeyword.trim()
        ? {
            name: { $like: `%${searchKeyword.trim()}%` },
          }
        : undefined,
    },
    { enabled: active },
  );

  React.useEffect(() => {
    const batch = (query.data?.items as MediaItem[] | undefined) ?? [];
    if (page === 1) {
      setAccumulated(batch);
      return;
    }
    setAccumulated((prev) => {
      const seen = new Set(prev.map((i) => String(i.id)));
      const next = [...prev];
      for (const it of batch) {
        const key = String(it.id);
        if (seen.has(key)) continue;
        seen.add(key);
        next.push(it);
      }
      return next;
    });
  }, [page, query.data]);

  React.useEffect(() => {
    setLoadedImageIds((prev) => {
      if (prev.size === 0) return prev;
      const currentIds = new Set(accumulated.map((i) => String(i.id)));
      const next = new Set<string>();
      for (const id of prev) {
        if (currentIds.has(id)) next.add(id);
      }
      return next;
    });
  }, [accumulated]);

  const hasNext = query.data?.hasNext ?? false;
  const isFetching = query.isFetching;
  loadMoreGateRef.current = { hasNext, isFetching };

  React.useEffect(() => {
    if (!active) return;
    const root = scrollRootRef?.current ?? internalScrollEl;
    const sentinel = loadMoreSentinelRef.current;
    if (!root || !sentinel) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const hit = entries.some((e) => e.isIntersecting);
        const gate = loadMoreGateRef.current;
        if (!hit || !gate.hasNext || gate.isFetching) return;
        setPage((p) => p + 1);
      },
      { root, rootMargin: '320px', threshold: 0 },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [active, accumulated.length, hasNext, scrollRootRef, internalScrollEl]);

  const masonryBlock = (
    <>
      <Masonry
        key={masonryColumnCount}
        breakpointCols={masonryColumnCount}
        className="select-my-image-masonry-grid"
        columnClassName="select-my-image-masonry-grid_column"
      >
        {query.isFetching && accumulated.length === 0
          ? Array.from({ length: GALLERY_PAGE_SIZE }).map((_, idx) => (
              <div
                key={`gallery-skeleton-${idx}`}
                className="mb-4 w-full overflow-hidden rounded-lg border border-border bg-muted"
                aria-hidden
              >
                <div
                  className="relative min-h-32 w-full animate-pulse bg-muted/50"
                  style={checkerboardStyle}
                />
              </div>
            ))
          : accumulated.map((item) => {
              const id = String(item.id);
              const src = (item.url ?? '').trim();
              const titleLabel =
                typeof item.name === 'string' && item.name.trim() ? item.name.trim() : '未命名';
              const loaded = loadedImageIds.has(id);
              return (
                <button
                  key={id}
                  type="button"
                  className="group relative mb-4 w-full overflow-hidden rounded-lg border border-border bg-muted text-left transition-shadow hover:shadow-md"
                  onClick={() => onPickImage(src)}
                  disabled={!src}
                >
                  <div className="relative min-h-24 w-full overflow-hidden" style={checkerboardStyle}>
                    {src ? (
                      <>
                        <img
                          src={src}
                          alt={titleLabel}
                          className="block h-auto w-full object-contain transition-opacity duration-200"
                          style={{ opacity: loaded ? 1 : 0 }}
                          loading="lazy"
                          onLoad={() =>
                            setLoadedImageIds((prev) => {
                              if (prev.has(id)) return prev;
                              const next = new Set(prev);
                              next.add(id);
                              return next;
                            })
                          }
                          onError={() =>
                            setLoadedImageIds((prev) => {
                              if (prev.has(id)) return prev;
                              const next = new Set(prev);
                              next.add(id);
                              return next;
                            })
                          }
                        />
                        {!loaded ? (
                          <div
                            className="pointer-events-none absolute inset-0 animate-pulse bg-muted/45"
                            aria-hidden
                          />
                        ) : null}
                      </>
                    ) : (
                      <div className="h-24 w-full" />
                    )}
                    <div
                      className="pointer-events-none absolute inset-x-0 bottom-0 z-10 translate-y-full bg-invert/80 px-2.5 py-2 text-left text-xs font-medium text-invert-foreground transition-transform duration-200 ease-out group-hover:translate-y-0 group-focus-within:translate-y-0"
                      aria-hidden
                    >
                      <span className="line-clamp-2">{titleLabel}</span>
                    </div>
                  </div>
                </button>
              );
            })}
      </Masonry>
      {accumulated.length > 0 ? <div ref={loadMoreSentinelRef} className="h-6" aria-hidden /> : null}
      {!hasNext && accumulated.length > 0 ? (
        <p className="py-2 text-center text-xs text-muted-foreground">已加载全部</p>
      ) : null}
    </>
  );

  return (
    <>
      <style>{`
        .select-my-image-masonry-grid { display: flex; margin-left: -1rem; width: auto; }
        .select-my-image-masonry-grid_column { padding-left: 1rem; background-clip: padding-box; }
      `}</style>
      {scrollRootRef ? (
        <div
          ref={measureRef}
          className={innerClassName ? `w-full min-w-0 ${innerClassName}` : 'w-full min-w-0'}
        >
          {masonryBlock}
        </div>
      ) : (
        <div
          ref={setInternalScrollEl}
          className="min-h-0 flex-1 overflow-y-auto p-6 pt-4"
        >
          <div
            ref={measureRef}
            className={innerClassName ? `w-full min-w-0 ${innerClassName}` : 'w-full min-w-0'}
          >
            {masonryBlock}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * 仅从「我的图库」选图或本地上传，逻辑对齐 `ImageSelectPopover`（无链接 Tab）。
 * 不包含裁切步骤，可与 `SelectMyImageCropDialog` 组合使用。
 */
export function SelectMyImageListDialog({
  open,
  onOpenChange,
  onImageUrlChosen,
}: SelectMyImageListDialogProps) {
  const [tab, setTab] = React.useState<TabKey>('gallery');
  const [searchKeyword, setSearchKeyword] = React.useState('');
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [uploadTabState, setUploadTabState] = React.useState<UploadTabState>({ status: 'idle' });
  const uploadPreviewFile =
    uploadTabState.status === 'uploading' || uploadTabState.status === 'error'
      ? uploadTabState.file
      : null;
  const uploadObjectUrl = useObjectUrl(uploadPreviewFile);

  React.useEffect(() => {
    if (!open) return;
    setTab('gallery');
    setSearchKeyword('');
    setUploadTabState({ status: 'idle' });
  }, [open]);

  const utils = trpc.useUtils();
  const authorizeUploadMutation = trpc.assets.authorizeUpload.useMutation();
  const createMediaMutation = trpc.assets.create.useMutation();

  const handleUploadClick = React.useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = React.useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) return;

      e.target.value = '';
      setUploadTabState({ status: 'uploading', file, percent: 0 });
      try {
        const { objectKey } = await authorizeUploadMutation.mutateAsync({
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          fileSize: file.size,
        });
        await ensureAnonymousAuth(CLOUDBASE_ENV_ID);
        const result = await uploadFileDirect(
          { envId: CLOUDBASE_ENV_ID },
          file,
          {
            path: objectKey,
            onProgress: (p) => {
              setUploadTabState((prev) =>
                prev.status === 'uploading' ? { ...prev, percent: p.percent } : prev,
              );
            },
          },
        );
        const media = await createMediaMutation.mutateAsync({
          name: file.name,
          fileId: result.fileId,
          fileSize: file.size,
          mimeType: file.type || 'application/octet-stream',
          type: getMediaTypeForMedia(file.type, file.name),
          isOwner: true,
        });
        void utils.assets.listMy.invalidate();
        setUploadTabState({ status: 'idle' });
        onImageUrlChosen((media as { url?: string | null }).url ?? '');
      } catch (err) {
        setUploadTabState({
          status: 'error',
          file,
          message: err instanceof Error ? err.message : '上传失败',
        });
      }
    },
    [authorizeUploadMutation, createMediaMutation, onImageUrlChosen, utils.assets.listMy],
  );

  return (
    <Modal isOpen={open} onOpenChange={onOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside">
          <Modal.Dialog className="z-[1200] flex h-[75vh] w-[65vw] max-h-[75vh] max-w-[min(65vw,calc(100vw-2rem))] min-w-0 flex-col gap-0 overflow-hidden p-0">
            <Modal.Header className="shrink-0 border-b px-6 py-4">
              <Modal.Heading>选择图片</Modal.Heading>
            </Modal.Header>

            <Modal.Body className="flex min-h-0 flex-1 flex-col gap-0 p-0">
              <Tabs
                selectedKey={tab}
                onSelectionChange={(key) => {
                  if (key == null) return;
                  setTab(key as TabKey);
                }}
                className="flex min-h-0 flex-1 flex-col"
              >
                <div className="shrink-0 border-b border-border/60 px-6 pt-2 pb-3">
                  <div className="flex items-center gap-3">
                    <Tabs.ListContainer className="min-w-0">
                      <Tabs.List aria-label="图片来源" className="grid w-full max-w-72 grid-cols-2 gap-1">
                        <Tabs.Tab id="gallery">
                          我的图库
                          <Tabs.Indicator />
                        </Tabs.Tab>
                        <Tabs.Tab id="upload">
                          上传
                          <Tabs.Indicator />
                        </Tabs.Tab>
                      </Tabs.List>
                    </Tabs.ListContainer>
                    <InputGroup className="min-w-0 flex-1">
                      <InputGroup.Prefix className="pointer-events-none text-muted-foreground">
                        <IconSearch className="size-4" />
                      </InputGroup.Prefix>
                      <InputGroup.Input
                        placeholder="搜索图片名称"
                        value={searchKeyword}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchKeyword(e.target.value)}
                        disabled={tab !== 'gallery'}
                      />
                    </InputGroup>
                  </div>
                </div>

                <Tabs.Panel id="gallery" className="mt-0 flex min-h-0 flex-1 flex-col overflow-hidden">
                  <SelectMyImageGalleryPanel
                    active={open && tab === 'gallery'}
                    listHostOpen={open}
                    searchKeyword={searchKeyword}
                    onPickImage={onImageUrlChosen}
                  />
                </Tabs.Panel>

                <Tabs.Panel
                  id="upload"
                  className="mt-0 flex min-h-0 flex-1 flex-col overflow-hidden px-6 pb-6 pt-4"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  <div className="flex min-h-0 flex-1 flex-col">
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border-2 border-dashed border-border/70 bg-muted/10">
                      {uploadTabState.status === 'idle' ? (
                        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-6 p-6">
                          <IconPhotoPlus className="size-10 text-muted-foreground" />
                          <Button type="button" variant="primary" onPress={handleUploadClick}>
                            点击上传图片
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div
                            className="relative min-h-0 flex-1 overflow-hidden"
                            style={checkerboardStyle}
                          >
                            {uploadObjectUrl ? (
                              <img
                                src={uploadObjectUrl}
                                alt={uploadTabState.file.name}
                                className="absolute inset-0 size-full object-contain"
                              />
                            ) : (
                              <div className="flex size-full items-center justify-center text-xs text-muted-foreground">
                                准备中…
                              </div>
                            )}
                          </div>
                          <div className="shrink-0 space-y-2 border-t border-border/60 bg-background/80 px-4 py-3">
                            <div className="truncate text-xs text-foreground">{uploadTabState.file.name}</div>
                            {uploadTabState.status === 'uploading' ? (
                              <ProgressBar
                                aria-label="上传进度"
                                value={uploadTabState.percent}
                                minValue={0}
                                maxValue={100}
                                className="w-full"
                              >
                                <ProgressBar.Track className="h-1.5 rounded-full bg-default-200">
                                  <ProgressBar.Fill className="h-full rounded-full bg-primary" />
                                </ProgressBar.Track>
                              </ProgressBar>
                            ) : (
                              <p className="text-xs text-destructive">{uploadTabState.message}</p>
                            )}
                            <div className="flex flex-wrap gap-2">
                              {uploadTabState.status === 'error' ? (
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  onPress={() => setUploadTabState({ status: 'idle' })}
                                >
                                  关闭
                                </Button>
                              ) : null}
                              <Button
                                type="button"
                                variant={uploadTabState.status === 'error' ? 'primary' : 'secondary'}
                                size="sm"
                                onPress={handleUploadClick}
                                isDisabled={uploadTabState.status === 'uploading'}
                              >
                                {uploadTabState.status === 'uploading' ? '上传中…' : '重新选择'}
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </Tabs.Panel>
              </Tabs>
            </Modal.Body>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}

/** 与富文本 `ImageCropDialog` 一致的比例选项 */
export type CropRatioPresetKey = 'free' | '16:9' | '1:1' | '3:4' | '4:3' | '21:9';

export const CROP_RATIO_PRESETS: Array<{
  key: CropRatioPresetKey;
  label: string;
  mode: 'free' | 'custom';
  aspectRatio?: number;
}> = [
  { key: 'free', label: '任意', mode: 'free' },
  { key: '16:9', label: '16:9', mode: 'custom', aspectRatio: 16 / 9 },
  { key: '1:1', label: '1:1', mode: 'custom', aspectRatio: 1 },
  { key: '3:4', label: '3:4', mode: 'custom', aspectRatio: 3 / 4 },
  { key: '4:3', label: '4:3', mode: 'custom', aspectRatio: 4 / 3 },
  { key: '21:9', label: '21:9', mode: 'custom', aspectRatio: 21 / 9 },
];

function isFixedCropAspect(ratio: number | undefined): ratio is number {
  return ratio != null && Number.isFinite(ratio) && ratio > 0;
}

export type SelectMyImageCropDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageUrl: string;
  /**
   * 裁切宽高比 **宽/高**（如 21:9 → `21/9`）。
   * 传入有效正数时锁定该比例并**隐藏**比例选项；不传则显示比例按钮，默认「任意」。
   */
  aspectRatio?: number;
  onConfirm: (norm: SelectMyImageCropNorm) => void;
  initialNormalizedCrop?: SelectMyImageCropNorm | null;
  /** 主按钮文案，默认「确定」 */
  confirmLabel?: string;
  /** 顶部说明；默认知识库选图场景文案 */
  description?: string;
  contentClassName?: string;
  /** 为 true 时在弹窗内容上设置 `data-image-crop-dialog` */
  dataImageCropDialog?: boolean;
};

const DEFAULT_CROP_DESCRIPTION = '请移动或缩小选区，选择所展现的图片范围';

const DEFAULT_CROP_CONTENT_CLASS =
  'z-[1250] flex max-h-[min(90vh,820px)] min-h-0 w-full max-w-3xl flex-col gap-0 overflow-hidden p-0';

/**
 * 图片裁切弹窗，基于 `@hs/ui` 的 `ImageCropper`。
 * 可传入固定 `aspectRatio`（隐藏比例选项），或由用户在「任意 / 16:9 / …」中选择。
 */
export function SelectMyImageCropDialog({
  open,
  onOpenChange,
  imageUrl,
  aspectRatio: aspectRatioProp,
  onConfirm,
  initialNormalizedCrop = null,
  confirmLabel = '确定',
  description = DEFAULT_CROP_DESCRIPTION,
  contentClassName = DEFAULT_CROP_CONTENT_CLASS,
  dataImageCropDialog = false,
}: SelectMyImageCropDialogProps) {
  const fixedAspect = isFixedCropAspect(aspectRatioProp);
  const [ratioKey, setRatioKey] = React.useState<CropRatioPresetKey>('free');
  const [cropError, setCropError] = React.useState<string | null>(null);
  const cropperRef = React.useRef<ImageCropperRef>(null);

  const preset = React.useMemo(() => {
    if (fixedAspect) {
      return { mode: 'custom' as const, aspectRatio: aspectRatioProp };
    }
    return CROP_RATIO_PRESETS.find((p) => p.key === ratioKey) ?? CROP_RATIO_PRESETS[0];
  }, [fixedAspect, aspectRatioProp, ratioKey]);

  React.useEffect(() => {
    if (!open) return;
    setCropError(null);
    if (!fixedAspect) setRatioKey('free');
  }, [open, imageUrl, fixedAspect]);

  const handleCancel = React.useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleConfirm = React.useCallback(() => {
    if (!imageUrl.trim()) return;
    const norm = cropperRef.current?.getNormalizedCropRect();
    if (!norm) {
      setCropError('当前无法读取裁切框：请保持旋转为 0°，并确保裁切区有效。');
      return;
    }
    onConfirm(norm);
  }, [imageUrl, onConfirm]);

  const cropperKey =
    open && imageUrl
      ? `${imageUrl}-${fixedAspect ? `fixed-${aspectRatioProp}` : ratioKey}`
      : 'closed';

  return (
    <Modal
      isOpen={open}
      onOpenChange={(next) => {
        if (!next) handleCancel();
      }}
    >
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside">
          <Modal.Dialog
            className={contentClassName}
            data-image-crop-dialog={dataImageCropDialog ? 'true' : undefined}
          >
            <Modal.Header className="flex-row items-center justify-between gap-4 px-6 py-4">
              <Modal.Heading className="text-lg">图片裁切</Modal.Heading>
              <div className="flex items-center gap-2">
                <Button type="button" variant="secondary" className="bg-background" onPress={handleCancel}>
                  取消
                </Button>
                <Button type="button" onPress={handleConfirm} isDisabled={!imageUrl.trim()}>
                  {confirmLabel}
                </Button>
              </div>
            </Modal.Header>

            <Modal.Body className="min-h-0 flex-1 overflow-auto px-6 py-4">
              <p className="pb-3 text-muted-foreground">{description}</p>
              {!fixedAspect ? (
                <div className="flex flex-wrap items-center gap-2 pb-4">
                  {CROP_RATIO_PRESETS.map((p) => {
                    const active = p.key === ratioKey;
                    return (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => setRatioKey(p.key)}
                        className={[
                          'flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm transition-colors',
                          active ? 'border-primary bg-primary/10' : 'bg-background hover:bg-muted/30',
                        ].join(' ')}
                      >
                        <span className="font-medium">{p.label}</span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
              {cropError ? <p className="pb-2 text-sm text-destructive">{cropError}</p> : null}
              <div className="overflow-hidden rounded-md border border-border">
                <ImageCropper
                  key={cropperKey}
                  ref={cropperRef}
                  image={imageUrl}
                  initialNormalizedCrop={initialNormalizedCrop}
                  mode={preset.mode}
                  aspectRatio={preset.mode === 'custom' ? preset.aspectRatio : undefined}
                  className="h-[min(62vh,560px)] min-h-[360px] w-full"
                />
              </div>
            </Modal.Body>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}

export type SelectMyImageProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * 裁切宽高比 **宽/高**（如 21:9 → `21/9`）。
   * 传入有效正数时，选图或上传成功后先打开固定比例裁切，确定后再 `onPick`；不传则直接 `onPick`，`cropNorm` 为 `null`。
   */
  cropAspectRatio?: number;
  onPick: (pick: SelectMyImagePick) => void;
};

/**
 * 组合「选图 / 上传」与可选的固定比例裁切，行为与拆分前一致。
 */
export function SelectMyImage({ open, onOpenChange, onPick, cropAspectRatio }: SelectMyImageProps) {
  const [cropStepOpen, setCropStepOpen] = React.useState(false);
  const [pendingCropUrl, setPendingCropUrl] = React.useState('');

  const needsCrop =
    cropAspectRatio != null && Number.isFinite(cropAspectRatio) && cropAspectRatio > 0;

  React.useEffect(() => {
    if (!open) {
      setCropStepOpen(false);
      setPendingCropUrl('');
    }
  }, [open]);

  const finishPick = React.useCallback(
    (url: string, cropNorm: SelectMyImageCropNorm | null) => {
      const trimmed = url.trim();
      if (!trimmed) return;
      onPick({ url: trimmed, cropNorm });
      onOpenChange(false);
      setCropStepOpen(false);
      setPendingCropUrl('');
    },
    [onOpenChange, onPick],
  );

  const onImageUrlChosen = React.useCallback(
    (url: string) => {
      const trimmed = url.trim();
      if (!trimmed) return;
      if (needsCrop && cropAspectRatio != null) {
        setPendingCropUrl(trimmed);
        setCropStepOpen(true);
      } else {
        finishPick(trimmed, null);
      }
    },
    [needsCrop, cropAspectRatio, finishPick],
  );

  return (
    <>
      <SelectMyImageListDialog
        open={open}
        onOpenChange={onOpenChange}
        onImageUrlChosen={onImageUrlChosen}
      />
      {needsCrop && cropAspectRatio != null ? (
        <SelectMyImageCropDialog
          open={cropStepOpen}
          onOpenChange={(next) => {
            if (!next) {
              setCropStepOpen(false);
              setPendingCropUrl('');
            }
          }}
          imageUrl={pendingCropUrl}
          aspectRatio={cropAspectRatio}
          onConfirm={(norm) => finishPick(pendingCropUrl, norm)}
        />
      ) : null}
    </>
  );
}
